import {
  Button as HeadlessButton,
  type ButtonProps as HeadlessButtonProps,
} from "@headlessui/react";
import clsx from "clsx";
import React from "react";
import { TouchTarget } from "./button";
import { Link } from "./link";
import Image from "next/image";

type AvatarProps = {
  src?: string | null;
  square?: boolean;
  initials?: string;
  alt?: string;
  className?: string;
  contain?: boolean;
};

export function Avatar({
  src = null,
  square = false,
  contain = false,
  initials,
  alt = "",
  className,
  ...props
}: AvatarProps & React.ComponentPropsWithoutRef<"span">) {
  return (
    <span
      data-slot="avatar"
      className={clsx(
        className,

        // Basic layout
        "inline-grid align-middle *:col-start-1 *:row-start-1",

        // Add the correct border radius
        square ? "rounded *:rounded" : "rounded-full *:rounded-full",
        contain ? "border *: 1px dotted" : "",
      )}
      {...props}>
      {initials && (
        <svg
          className="select-none fill-zinc-800 text-[48px] font-medium uppercase"
          viewBox="0 0 100 100"
          aria-hidden={alt ? undefined : "true"}>
          {alt && <title>{alt}</title>}
          <text
            x="50%"
            y="50%"
            alignmentBaseline="middle"
            dominantBaseline="middle"
            textAnchor="middle"
            dy=".125em">
            {initials}
          </text>
        </svg>
      )}
      {src && (
        <Image
          src={src}
          alt={alt}
          width={"100"}
          height={"100"}
          style={{
            objectFit: contain ? "contain" : "cover",
            height: "100%",
            width: "100%",
            overflow: "hidden",
          }}
          unoptimized={true}
        />
      )}
    </span>
  );
}

export const AvatarButton = React.forwardRef(function AvatarButton(
  {
    src,
    square = false,
    initials,
    alt,
    className,
    ...props
  }: AvatarProps &
    (HeadlessButtonProps | React.ComponentPropsWithoutRef<typeof Link>),
  ref: React.ForwardedRef<HTMLElement>,
) {
  const classes = clsx(
    className,
    square ? "rounded-lg" : "rounded-full",
    "relative focus:outline-none data-[focus]:outline data-[focus]:outline-2 data-[focus]:outline-offset-2 data-[focus]:outline-blue-500",
  );

  return "href" in props ? (
    <Link
      {...props}
      className={classes}
      ref={ref as React.ForwardedRef<HTMLAnchorElement>}>
      <TouchTarget>
        <Avatar src={src} square={square} initials={initials} alt={alt} />
      </TouchTarget>
    </Link>
  ) : (
    <HeadlessButton {...props} className={classes} ref={ref}>
      <TouchTarget>
        <Avatar src={src} square={square} initials={initials} alt={alt} />
      </TouchTarget>
    </HeadlessButton>
  );
});
